import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, getErrorMessage, patch, post } from '../api/client';
import './SurveyResults.css';

const BUCKETS = [
  ['quick', 'Quick wins'],
  ['shortTerm', 'Short-term'],
  ['other', 'Other'],
];

const PIPELINE_LABELS = {
  'bring in': 'Bring in',
  'keep in': 'Keep in',
  multiply: 'Multiply',
};

function formatWhen(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const iso = date.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

function hasPublishableQuestions(aggregate) {
  return (aggregate?.modules || []).some((mod) => (
    !mod.suppressed && (mod.questions || []).some((question) => !question.suppressed)
  ));
}

function QuestionResults({ question }) {
  if (question.suppressed) {
    return (
      <article className="survey-results-question">
        <h3>{question.id}. {question.text}</h3>
        <p className="survey-results-suppressed">{question.message || 'Not enough responses yet'}</p>
      </article>
    );
  }

  const written = question.type === 'open' || question.type === 'comment'
    ? question.texts || []
    : question.fillInTexts || [];

  return (
    <article className="survey-results-question">
      <h3>{question.id}. {question.text}</h3>
      {question.subsectionCode && (
        <p className="survey-results-meta">{question.subsectionCode}</p>
      )}
      {(question.type === 'choice' || question.type === 'multi') && (
        <ul className="survey-results-options">
          {(question.options || []).map((option) => (
            <li key={option.key}>
              <div className="survey-results-option-label">
                <span>{option.text}</span>
                <span>{option.count} · {option.percent}%</span>
              </div>
              <div className="survey-results-bar" aria-hidden="true">
                <div className="survey-results-bar-fill" style={{ width: `${option.percent}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
      {written.length > 0 && (
        <ul className="survey-results-texts">
          {written.map((text, index) => (
            <li key={`${question.id}-${index}`}>{text}</li>
          ))}
        </ul>
      )}
    </article>
  );
}

function ReportList({ items }) {
  if (!items.length) return <p className="survey-results-meta">None in this group.</p>;
  return (
    <ul className="survey-results-report-list">
      {items.map((item) => (
        <li key={item.title} className="survey-results-report-card">
          <h3>{item.title}</h3>
          {item.why && <p>{item.why}</p>}
          <p className="survey-results-meta">
            {[
              item.flywheel ? `Section ${item.flywheel}` : '',
              item.pipeline ? PIPELINE_LABELS[item.pipeline] || item.pipeline : '',
              item.sourceQuestions?.length ? `Questions ${item.sourceQuestions.join(', ')}` : '',
            ].filter(Boolean).join(' · ')}
          </p>
          {item.metric && <p className="survey-results-meta">Metric: {item.metric}</p>}
        </li>
      ))}
    </ul>
  );
}

export function SurveyResultsView({ results, onStatus, onGenerate, busy, error }) {
  const [moduleKey, setModuleKey] = useState('');
  const modules = results?.aggregate?.modules || [];
  const visible = moduleKey ? modules.filter((mod) => mod.key === moduleKey) : modules;
  const closed = results?.status === 'closed';
  const canGenerate = Boolean(results?.aiConfigured) && hasPublishableQuestions(results?.aggregate);

  const changeStatus = () => {
    const next = closed ? 'open' : 'closed';
    const message = closed
      ? 'Reopen this survey? People can change their answers again.'
      : 'Close this survey? People can still read their own answers, but they cannot change them.';
    if (!window.confirm(message)) return;
    onStatus(next);
  };

  return (
    <div className="survey-results">
      <PageHeader title="Survey results" section="leaders" />
      <p className="survey-results-lead">
        Totals only. Names and individual answer sheets are not shown. A question or whole module stays hidden until at least 5 people have answered it.
      </p>
      <p className="survey-results-links">
        <Link to="/admin/survey/progress">View progress</Link>
        <Link to="/admin/survey-edits">Edit questions</Link>
      </p>
      <p className="survey-results-meta">
        Version {results.surveyVersion} · {closed ? 'Closed' : 'Open'}
      </p>
      <button type="button" className="survey-results-secondary" onClick={changeStatus} disabled={busy === 'status'}>
        {closed ? 'Reopen survey' : 'Close survey'}
      </button>

      <section id="survey-aggregates" aria-label="Grouped answers">
        <label className="survey-results-filter">
          <span>Module</span>
          <select value={moduleKey} onChange={(event) => setModuleKey(event.target.value)}>
            <option value="">All modules</option>
            {modules.map((mod) => (
              <option key={mod.key} value={mod.key}>{mod.key}. {mod.title}</option>
            ))}
          </select>
        </label>
        {visible.map((mod) => (
          <section key={mod.key} className="survey-results-module" aria-label={`${mod.key}. ${mod.title}`}>
            <h2>{mod.key}. {mod.title}</h2>
            {mod.suppressed ? (
              <p className="survey-results-suppressed">{mod.message || 'Not enough responses yet'}</p>
            ) : (
              <>
                <p className="survey-results-meta">{mod.respondents} people answered</p>
                {(mod.questions || []).map((question) => (
                  <QuestionResults key={question.id} question={question} />
                ))}
              </>
            )}
          </section>
        ))}
      </section>

      <section id="survey-recommendations" className="survey-results-report" aria-label="Recommendations">
        <h2>Recommendations</h2>
        {!results.aiConfigured && <p role="status">AI not configured</p>}
        {results.aiConfigured && !hasPublishableQuestions(results.aggregate) && (
          <p role="status">Not enough responses yet</p>
        )}
        <button type="button" className="survey-results-generate" onClick={onGenerate} disabled={!canGenerate || busy === 'generate'}>
          {busy === 'generate' ? 'Generating…' : 'Generate recommendations'}
        </button>
        {results.report ? (
          <>
            <p className="survey-results-meta">
              Last report for version {results.report.surveyVersion}
              {results.report.generatedAt ? `, ${formatWhen(results.report.generatedAt)}` : ''}.
            </p>
            {BUCKETS.map(([key, label]) => (
              <div key={key}>
                <h3 className="survey-results-bucket">{label}</h3>
                <ReportList items={results.report[key] || []} />
              </div>
            ))}
          </>
        ) : (
          <p className="survey-results-meta">No report yet. Generate one when you are ready.</p>
        )}
      </section>
      {error && <p className="survey-results-error" role="alert">{error}</p>}
    </div>
  );
}

const SurveyResultsPage = () => {
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    let cancelled = false;
    get('/api/leaders-survey/results')
      .then((response) => {
        if (cancelled) return;
        if (!response.ok) {
          setError(getErrorMessage(response, 'Failed to load results'));
          setLoading(false);
          return;
        }
        setResults(response.data);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load results');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onStatus = async (status) => {
    setBusy('status');
    setActionError('');
    const response = await patch('/api/leaders-survey/status', {
      status,
      surveyVersion: results.surveyVersion,
    });
    setBusy('');
    if (!response.ok) {
      setActionError(getErrorMessage(response, 'Failed to update survey status'));
      return;
    }
    setResults((current) => ({
      ...current,
      status: response.data.status,
      closedAt: response.data.closedAt ?? current.closedAt,
    }));
  };

  const onGenerate = async () => {
    setBusy('generate');
    setActionError('');
    const response = await post('/api/leaders-survey/recommendations', {
      surveyVersion: results.surveyVersion,
    });
    setBusy('');
    if (!response.ok) {
      setActionError(getErrorMessage(response, 'Recommendations could not be generated.'));
      return;
    }
    if (response.data?.aiConfigured === false) {
      setResults((current) => ({ ...current, aiConfigured: false }));
      return;
    }
    setResults((current) => ({
      ...current,
      aiConfigured: true,
      report: response.data.report,
    }));
  };

  if (loading) return <div className="survey-results"><p>Loading…</p></div>;
  if (error || !results) {
    return (
      <div className="survey-results">
        <PageHeader title="Survey results" section="leaders" />
        <p className="survey-results-error" role="alert">{error || 'The survey is not available yet.'}</p>
      </div>
    );
  }
  return (
    <SurveyResultsView
      results={results}
      onStatus={onStatus}
      onGenerate={onGenerate}
      busy={busy}
      error={actionError}
    />
  );
};

export default SurveyResultsPage;
